import { DOCUMENT } from '@angular/common';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Angulartics2 } from 'angulartics2';
import { NGXLogger } from 'ngx-logger';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorModalService } from '../error-modal/error-modal-service';
import { HttpParamsOptions } from './IHttpParamsOptions';

export class CustomRequestConfig<T> extends HttpRequest<T> {
  locallyHandledErrors?: (responseError: HttpErrorResponse) => boolean | number[];
}

export interface IGlobalHttpErrorResponse extends HttpErrorResponse {
  wasHandledGlobally?: boolean;
}

export class InterceptorHttpParams extends HttpParams {
  constructor(
    public interceptorConfig: { statusCodesToIgnore?: ((responseError: HttpErrorResponse) => boolean) | number[] },
    params?: Record<string, string | string[]>,
  ) {
    super({ fromObject: params } as HttpParamsOptions); // Passes through the HttpParams
  }
}

@Injectable({
  providedIn: 'root',
})
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private _errorModalService: ErrorModalService,
    private _angulartics: Angulartics2,
    private _router: Router,
    private _logger: NGXLogger,
    @Inject(DOCUMENT) private _document: Document,
  ) {}

  // The HttpInterceptor interface declares the request parameter as type any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public intercept(request: CustomRequestConfig<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err: IGlobalHttpErrorResponse) => {
        if (request.params instanceof InterceptorHttpParams) {
          if (Array.isArray(request.params.interceptorConfig.statusCodesToIgnore)) {
            /*Iterate Array*/
            if (request.params.interceptorConfig.statusCodesToIgnore.some((errorCode) => errorCode === err.status)) {
              return throwError(() => err);
            }
          } else if (request.params.interceptorConfig.statusCodesToIgnore?.(err)) {
            return throwError(() => err);
          }
        }
        if (err.status !== 401) {
          if (err.status === 403) {
            this._logger.error('EC-403:User tried to login in wrong portal.');
            this._router.navigate(['access-denied']);
          } else if (err.status === 503) {
            this._logger.error('EC-503:Service unavailable');
            this._router.navigate(['service-unavailable']);
          } else {
            this._openModal(err);
          }
          err.wasHandledGlobally = true;
          // Google only takes a description.
          // See https://github.com/angulartics/angulartics2/blob/master/src/lib/providers/ga/ga.ts
          this._angulartics.exceptionTrack.next({
            description: [err.name, err.status, err.statusText, err.url, err.message].join('|'),
          });
        } else {
          this._document.location.assign('/login');
        }
        return throwError(() => err);
      }),
    );
  }

  private _openModal(errorResponse: HttpErrorResponse): Observable<NgbModalRef> {
    return this._errorModalService.open(errorResponse);
  }
}
